import { useAtomValue } from 'jotai';
import { ReactElement, useCallback } from 'react';
import { Keys } from '../../config';
import { SessionTimeout, TriggerState, validOrderCombosAtom } from '../../state';
import {
  ExpiryType,
  InstrumentCategoryEnum,
  InventoryManagementMethod,
  LanguageType,
  ListedInstrument,
  NbSelectItemInfo,
  NbSelectItemOption,
  OrderType,
  PeriodType,
  ThemeMode,
  TradingSession,
} from '../../types';
import { useDictionary } from './useDictionary';

// TODO Remove the flags from here... This is not pretty.
type UseOptionsCommonParams = {
  FlagHu: string | ReactElement;
  FlagUs: string | ReactElement;
};

export const useOptionsCommon = ({ FlagHu, FlagUs }: UseOptionsCommonParams) => {
  const { formatDictionaryType, getDictionary } = useDictionary();
  const validOrderCombos = useAtomValue(validOrderCombosAtom);
  const getAllOrderTypeOptions = useCallback(
    (hints?: Record<OrderType, NbSelectItemInfo>): NbSelectItemOption<OrderType>[] => {
      return Object.values(OrderType).map(
        (value): NbSelectItemOption<OrderType> => ({
          id: value,
          name: formatDictionaryType(value, 'orderTypes'),
          info: hints?.[value],
        }),
      );
    },
    [formatDictionaryType],
  );

  const getInventoryManagementMethodOptions = useCallback(
    (
      nameTranslations: Record<InventoryManagementMethod, string>,
      hints?: Record<InventoryManagementMethod, NbSelectItemInfo>,
    ): NbSelectItemOption<InventoryManagementMethod>[] => {
      return Object.values(InventoryManagementMethod).map(
        (value): NbSelectItemOption<InventoryManagementMethod> => ({
          id: value,
          name: nameTranslations[value],
          info: hints?.[value],
        }),
      );
    },
    [],
  );

  const getTriggerStateOptions = useCallback(
    (
      nameTranslations: Record<TriggerState, string>,
      hints?: Record<TriggerState, NbSelectItemInfo>,
    ): NbSelectItemOption<TriggerState>[] => {
      return Object.values(TriggerState).map(
        (value): NbSelectItemOption<TriggerState> => ({
          id: value,
          name: nameTranslations[value],
          info: hints?.[value],
        }),
      );
    },
    [],
  );

  const getBetTradingSessionOptions = useCallback(
    (hints?: Record<TradingSession, NbSelectItemInfo>): NbSelectItemOption<TradingSession>[] => {
      return [
        TradingSession.All,
        TradingSession.UnderTrading,
        TradingSession.Auction,
        TradingSession.ClosingAuction,
        TradingSession.OpeningAuction,
        TradingSession.UnderTradingAuction,
      ].map(
        (value): NbSelectItemOption<TradingSession> => ({
          id: value,
          name: formatDictionaryType(value, 'tradingSessions'),
          info: hints?.[value],
        }),
      );
    },
    [formatDictionaryType],
  );

  const getXetradingSessionOptions = useCallback(
    (hints?: Record<TradingSession, NbSelectItemInfo>): NbSelectItemOption<TradingSession>[] => {
      return [TradingSession.UnderTrading].map(
        (value): NbSelectItemOption<TradingSession> => ({
          id: value,
          name: formatDictionaryType(value, 'tradingSessions'),
          info: hints?.[value],
        }),
      );
    },
    [formatDictionaryType],
  );

  const getUsaTradingSessionOptions = useCallback(
    (hints?: Record<TradingSession, NbSelectItemInfo>): NbSelectItemOption<TradingSession>[] => {
      return [TradingSession.Normal, TradingSession.PreMarket].map(
        (value): NbSelectItemOption<TradingSession> => ({
          id: value,
          name: formatDictionaryType(value, 'tradingSessions'),
          info: hints?.[value],
        }),
      );
    },
    [formatDictionaryType],
  );

  const getTradingSessionOptions = useCallback(
    (
      instrument: ListedInstrument | null | undefined,
      orderType?: OrderType,
      hints?: Record<TradingSession, NbSelectItemInfo>,
    ): NbSelectItemOption<TradingSession>[] => {
      if (!instrument || instrument.mic === Keys.Markets.Mic.Unknown || !orderType) {
        return [];
      }

      const filteredTradingSessions = new Set(
        validOrderCombos[instrument.mic]
          ?.filter(combo => combo.orderType === orderType)
          .map(combo => combo.tradingSession),
      );

      const tradingSessionOptions = getDictionary('tradingSessions');
      return tradingSessionOptions
        .filter(t => filteredTradingSessions.has(t.id as TradingSession))
        .map(({ id: unTypedId, name }): NbSelectItemOption<TradingSession> => {
          const id = unTypedId as TradingSession;
          return { id, name, info: hints?.[id] };
        });
    },
    [getDictionary, validOrderCombos],
  );

  const getOrderTypeOptions = useCallback(
    (
      instrument: ListedInstrument | null | undefined,
      hints?: Record<OrderType, NbSelectItemInfo>,
    ): NbSelectItemOption<OrderType>[] => {
      if (!instrument) {
        return [];
      }

      const filteredOrderTypes = new Set(validOrderCombos[instrument.mic]?.map(combo => combo.orderType));

      const orderTypeOptions = getDictionary('orderTypes');
      return orderTypeOptions
        .filter(t => filteredOrderTypes.has(t.id as OrderType))
        .map(({ id: unTypedId, name }): NbSelectItemOption<OrderType> => {
          const id = unTypedId as OrderType;
          return { id, name, info: hints?.[id] };
        });
    },
    [getDictionary, validOrderCombos],
  );

  const getAllExpiryTypeOptions = useCallback(
    (hints?: Record<ExpiryType, NbSelectItemInfo>): NbSelectItemOption<ExpiryType>[] => {
      return Object.values(ExpiryType).map(
        (value): NbSelectItemOption<ExpiryType> => ({
          id: value,
          name: formatDictionaryType(value, 'orderExpiryTypes'),
          info: hints?.[value],
        }),
      );
    },
    [formatDictionaryType],
  );

  const getExpiryTypeOptions = useCallback(
    (
      instrument: ListedInstrument | null | undefined,
      orderType?: OrderType,
      hints?: Record<ExpiryType, NbSelectItemInfo>,
    ): NbSelectItemOption<ExpiryType>[] => {
      if (!instrument || instrument.mic === Keys.Markets.Mic.Unknown || !orderType) {
        return [];
      }
      const expiryTypeOptions = getDictionary('orderExpiryTypes');

      // https://ersteinv.atlassian.net/browse/NN-7754
      // For futures only day order type is allowed when order type is Market or StopMarket, so we return only that option without filtering with valid combos
      if (
        instrument.instrumentCategory === InstrumentCategoryEnum.Futures &&
        (orderType === OrderType.Market || orderType === OrderType.StopMarket)
      ) {
        const dayOption = expiryTypeOptions.find(option => option.id === ExpiryType.Day);
        if (dayOption) {
          return [{ id: ExpiryType.Day, name: dayOption.name, info: hints?.[ExpiryType.Day] }];
        }
      }

      const filteredExpiryTypes = new Set(
        validOrderCombos[instrument.mic]?.filter(combo => combo.orderType === orderType).map(combo => combo.expiryType),
      );

      return expiryTypeOptions
        .filter(t => filteredExpiryTypes.has(t.id as ExpiryType))
        .map(({ id: unTypedId, name }): NbSelectItemOption<ExpiryType> => {
          const id = unTypedId as ExpiryType;
          return { id, name, info: hints?.[id] };
        });
    },
    [getDictionary, validOrderCombos],
  );

  const getSelectAllOption = useCallback(
    (name: string, hint?: NbSelectItemInfo): NbSelectItemOption<string> => ({
      id: Keys.NbSelect.SelectAllDefaultId,
      name,
      info: hint,
    }),
    [],
  );

  const getLanguageOptions = useCallback(
    (
      nameTranslations: Record<LanguageType, string>,
      hints?: Record<LanguageType, NbSelectItemInfo>,
    ): NbSelectItemOption<LanguageType>[] => {
      return Object.values(LanguageType).map(
        (value): NbSelectItemOption<LanguageType> => ({
          id: value,
          name: nameTranslations[value],
          logo: value === LanguageType.Hun ? FlagHu : FlagUs,
          info: hints?.[value],
        }),
      );
    },
    [FlagHu, FlagUs],
  );

  const getSessionTimeoutOptions = useCallback(
    (
      nameTranslations: Record<SessionTimeout, string>,
      hints?: Record<SessionTimeout, NbSelectItemInfo>,
    ): NbSelectItemOption<SessionTimeout>[] => {
      return Object.values(SessionTimeout)
        .map(
          (value): NbSelectItemOption<SessionTimeout> => ({
            id: value,
            name: nameTranslations[value],
            info: hints?.[value],
          }),
        )
        .filter(option => option.id !== SessionTimeout.TimeoutEndOfTheDay);
    },
    [],
  );

  const getDisplayModeOptions = useCallback(
    (
      nameTranslations: Record<ThemeMode, string>,
      hints?: Record<ThemeMode, NbSelectItemInfo>,
    ): NbSelectItemOption<ThemeMode>[] => {
      return Object.values(ThemeMode).map(
        (value): NbSelectItemOption<ThemeMode> => ({
          id: value,
          name: nameTranslations[value],
          info: hints?.[value],
        }),
      );
    },
    [],
  );

  const getPeriodOptions = useCallback(
    (
      nameTranslations: Record<PeriodType, string>,
      hints?: Record<PeriodType, NbSelectItemInfo>,
    ): NbSelectItemOption<PeriodType>[] => {
      return Object.values(PeriodType).map(
        (value): NbSelectItemOption<PeriodType> => ({
          id: value,
          name: nameTranslations[value],
          info: hints?.[value],
        }),
      );
    },
    [],
  );

  return {
    getAllOrderTypeOptions,
    getInventoryManagementMethodOptions,
    getTradingSessionOptions,
    getOrderTypeOptions,
    getExpiryTypeOptions,
    getAllExpiryTypeOptions,
    getSelectAllOption,
    getBetTradingSessionOptions,
    getUsaTradingSessionOptions,
    getXetradingSessionOptions,
    getTriggerStateOptions,
    getLanguageOptions,
    getSessionTimeoutOptions,
    getDisplayModeOptions,
    getPeriodOptions,
  };
};
